import { useEffect, useRef, useState } from 'react'
import { Modal } from '@/shared/ui/Modal'
import './CameraCapture.css'

/**
 * Take a photo with the computer's camera.
 *
 * Only used on desktop. A phone gets its own camera app instead — through a
 * file input with `capture`, which produces a far better picture than a video
 * frame and is the interface people already know. There is no equivalent on a
 * laptop, so this fills that gap rather than replacing anything.
 *
 * The preview is mirrored, because an unmirrored view of your own face is
 * disconcerting to frame — every video-call application does the same. The
 * captured frame is *not* mirrored: that would flip any text in the shot and
 * produce a photo that does not match the person.
 */
export function CameraCapture({
  onCancel,
  onCaptured,
}: {
  onCancel: () => void
  onCaptured: (file: File) => void
}) {
  const video = useRef<HTMLVideoElement>(null)
  const stream = useRef<MediaStream | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let live = true

    navigator.mediaDevices
      ?.getUserMedia({ video: { facingMode: 'user', width: 1280, height: 1280 }, audio: false })
      .then((s) => {
        if (!live) {
          s.getTracks().forEach((t) => t.stop())
          return
        }
        stream.current = s
        if (video.current) {
          video.current.srcObject = s
          void video.current.play()
        }
        setReady(true)
      })
      .catch((e: unknown) => {
        if (!live) return
        // Refusing the permission prompt is a decision, not a fault, and should
        // not read as one.
        const name = e instanceof DOMException ? e.name : ''
        setError(
          name === 'NotAllowedError'
            ? 'The browser blocked camera access. Allow it in the address bar, or choose a file instead.'
            : name === 'NotFoundError'
              ? 'No camera found on this computer.'
              : 'The camera could not be started.',
        )
      })

    return () => {
      live = false
      // Releases the hardware and turns the indicator light off. Without this
      // the camera stays on until the tab is closed.
      stream.current?.getTracks().forEach((t) => t.stop())
      stream.current = null
    }
  }, [])

  const take = () => {
    const v = video.current
    if (!v) return
    // Square, centred on the frame — the crop editor opens next, so this only
    // has to avoid throwing away the edges someone might want.
    const edge = Math.min(v.videoWidth, v.videoHeight)
    const canvas = document.createElement('canvas')
    canvas.width = edge
    canvas.height = edge
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.drawImage(v, (v.videoWidth - edge) / 2, (v.videoHeight - edge) / 2, edge, edge, 0, 0, edge, edge)
    canvas.toBlob((blob) => {
      if (blob) onCaptured(new File([blob], 'camera.jpg', { type: 'image/jpeg' }))
    }, 'image/jpeg', 0.92)
  }

  return (
    <Modal
      title="Take a photo"
      onClose={onCancel}
      width={340}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn btn--primary" onClick={take} disabled={!ready}>
            Capture
          </button>
        </>
      }
    >
      {error ? (
        <div className="recform__error">{error}</div>
      ) : (
        <div className="cam">
          <video ref={video} className="cam__video" playsInline muted />
        </div>
      )}
    </Modal>
  )
}
